/**
 * StudentDataFetcher - Fetches all student discipline data in ONE go
 *
 * Instead of each rule service making 5-10 queries, we fetch everything
 * once and pass it to all rules. No persistent caching, just fetch once
 * per mention evaluation.
 */

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import { disciplineLogger } from '../../../utils/disciplineLogger';
import { firebaseMonitor } from '../../../utils/firebaseMonitor';
import { contractCache } from '../core/ContractCache';
import { getDomains, PROTECTED_DOMAIN_DEFINITIONS } from '../../domainRegistry';
import { dynamicDomainListConstraints } from '../../domain/dynamicDomainQueries';

export class StudentDataFetcher {
  constructor() {
    // Separate caches for each collection to enable targeted invalidation
    this.mentionsCache = new Map();
    this.domainEventsCache = new Map();
    this.consequencesCache = new Map();
    this.accountabilityCache = new Map();
    this.contractsCache = new Map();
    this.messagesCache = new Map();
    this.CACHE_TTL = 300000; // 5 minutes - optimized for student profile viewing and rule evaluation
    this.SESSION_STORAGE_PREFIX = 'swan_student_data_';
    this.isParentUser = null; // Cache parent status
  }

  /**
   * Check if current user is a parent with linkedStudentIds
   * Users with staff/admin/dev permissions are NOT treated as parents,
   * even if they have parent: true (common for testing/demo accounts)
   */
  async checkIsParentWithLinks() {
    // NOTE: Removed cache early-return to ensure staff check always runs
    // Staff users should never be subject to parent restrictions
    try {
      const user = auth.currentUser;
      if (!user) {
        this.isParentUser = false;
        return false;
      }

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        this.isParentUser = false;
        return false;
      }

      const userData = userDoc.data();

      // If user has staff/admin/dev permissions, they can access all students
      // Parent restrictions only apply to parent-ONLY users
      const hasStaffAccess = userData.admin === true || userData.staff === true || userData.dev === true;
      if (hasStaffAccess) {
        disciplineLogger.debug('👤 User has staff access - skipping parent restrictions', {
          userId: user.uid,
          admin: userData.admin,
          staff: userData.staff,
          dev: userData.dev
        });
        this.isParentUser = false;
        return false;
      }

      // Only apply parent restrictions to parent-only users
      const isParent = userData.parent === true && Array.isArray(userData.linkedStudentIds);
      this.isParentUser = {
        isParent,
        linkedStudentIds: userData.linkedStudentIds || []
      };

      if (isParent) {
        disciplineLogger.info('🎯 Parent user detected with linked students', {
          userId: user.uid,
          linkedCount: userData.linkedStudentIds.length
        });
      }

      return this.isParentUser;
    } catch (error) {
      disciplineLogger.error('Error checking parent status', error);
      this.isParentUser = false;
      return false;
    }
  }

  /**
   * Save data to sessionStorage
   * Persists cache across page refreshes (within same tab/session)
   * @param {string} cacheType - Cache name (mentions, consequences, etc)
   * @param {string} studentId - Student ID
   * @param {any} data - Data to store
   */
  saveToSessionStorage(cacheType, studentId, data) {
    try {
      const key = `${this.SESSION_STORAGE_PREFIX}${cacheType}_${studentId}`;
      const cacheEntry = {
        data,
        timestamp: Date.now()
      };
      sessionStorage.setItem(key, JSON.stringify(cacheEntry));
    } catch (error) {
      // Handle quota exceeded or other errors - fail silently and fall back to memory-only cache
      if (error.name === 'QuotaExceededError') {
        disciplineLogger.warn(`sessionStorage quota exceeded for ${cacheType}`, { studentId });
      } else {
        disciplineLogger.warn(`Failed to save to sessionStorage for ${cacheType}`, { studentId, error: error.message });
      }
    }
  }

  /**
   * Load data from sessionStorage
   * @param {string} cacheType - Cache name (mentions, consequences, etc)
   * @param {string} studentId - Student ID
   * @returns {Object|null} Cached data with timestamp or null if not found/expired
   */
  loadFromSessionStorage(cacheType, studentId) {
    try {
      const key = `${this.SESSION_STORAGE_PREFIX}${cacheType}_${studentId}`;
      const cached = sessionStorage.getItem(key);

      if (!cached) return null;

      const cacheEntry = JSON.parse(cached);

      // Check if expired
      if (Date.now() - cacheEntry.timestamp > this.CACHE_TTL) {
        sessionStorage.removeItem(key);
        disciplineLogger.debug(`🗑️ Expired sessionStorage cache removed for ${cacheType}`, { studentId });
        return null;
      }

      return cacheEntry;
    } catch (error) {
      // Handle invalid JSON or other errors
      disciplineLogger.warn(`Failed to load from sessionStorage for ${cacheType}`, { studentId, error: error.message });
      // Clear corrupted cache
      try {
        const key = `${this.SESSION_STORAGE_PREFIX}${cacheType}_${studentId}`;
        sessionStorage.removeItem(key);
      } catch (e) {
        // Ignore cleanup errors
      }
      return null;
    }
  }

  /**
   * Clear sessionStorage for a specific cache type and student
   * @param {string} cacheType - Cache name (mentions, consequences, etc)
   * @param {string} studentId - Student ID
   */
  clearSessionStorage(cacheType, studentId) {
    try {
      const key = `${this.SESSION_STORAGE_PREFIX}${cacheType}_${studentId}`;
      sessionStorage.removeItem(key);
    } catch (error) {
      // Ignore errors
    }
  }

  /**
   * Helper: Get data from cache or fetch if needed
   * Now with sessionStorage persistence to survive page refreshes
   *
   * Cache hierarchy:
   * 1. Check memory cache (fastest)
   * 2. Check sessionStorage (survives page refresh)
   * 3. Fetch from Firestore (slowest)
   *
   * @param {string} cacheType - Cache name (mentions, consequences, etc)
   * @param {string} studentId - Student ID
   * @param {Function} fetchFn - Function to fetch data if not cached
   * @returns {Promise<any>} Cached or fresh data
   */
  async getCachedOrFetch(cacheType, studentId, fetchFn) {
    const cache = this[`${cacheType}Cache`];

    // 1. Check memory cache first (fastest)
    const memoryCached = cache.get(studentId);
    if (memoryCached && Date.now() - memoryCached.timestamp < this.CACHE_TTL) {
      const cacheAge = Date.now() - memoryCached.timestamp;
      disciplineLogger.debug(`📦 ${cacheType} MEMORY CACHE HIT for ${studentId}`, {
        cacheAgeMs: cacheAge,
        cacheAgeSec: (cacheAge / 1000).toFixed(1)
      });
      return memoryCached.data;
    }

    // 2. Check sessionStorage (survives page refresh)
    const sessionCached = this.loadFromSessionStorage(cacheType, studentId);
    if (sessionCached) {
      const cacheAge = Date.now() - sessionCached.timestamp;
      disciplineLogger.debug(`💾 ${cacheType} SESSION CACHE HIT for ${studentId}`, {
        cacheAgeMs: cacheAge,
        cacheAgeSec: (cacheAge / 1000).toFixed(1),
        savedReads: '~5-10 Firestore reads avoided'
      });

      // Restore to memory cache for faster subsequent access
      cache.set(studentId, sessionCached);

      return sessionCached.data;
    }

    // 3. Fetch fresh data from Firestore (cache miss)
    disciplineLogger.debug(`🔥 ${cacheType} CACHE MISS for ${studentId} - fetching from Firestore`);
    const data = await fetchFn();
    const timestamp = Date.now();
    const cacheEntry = { data, timestamp };

    // Store in memory cache
    cache.set(studentId, cacheEntry);

    // Store in sessionStorage for persistence across page refreshes
    this.saveToSessionStorage(cacheType, studentId, data);

    // Clean old entries from memory cache
    if (cache.size > 100) {
      const now = Date.now();
      for (const [key, value] of cache.entries()) {
        if (now - value.timestamp > this.CACHE_TTL) {
          cache.delete(key);
        }
      }
    }

    return data;
  }

  /**
   * Fetch all discipline data for a student in a single pass
   * Each collection is cached independently for targeted invalidation
   * @param {string} studentId - The student to fetch data for
   * @param {string} schoolId - The school ID for hierarchical queries
   * @param {string} [domainId='discipline'] - Which domain's events to surface
   *   as the flat `mentions` array. Defaults to 'discipline' so existing
   *   discipline callers are byte-identical.
   * @returns {Promise<Object>} All student discipline data
   */
  async fetchStudentData(studentId, schoolId, domainId = 'discipline') {
    disciplineLogger.info(`📊 Fetching student data (with per-collection caching)`, { studentId, schoolId });

    const startTime = Date.now();

    try {
      // Resolve the school's active domains (with legacy fallback for unseeded schools).
      // We pass these down so the events fetch can iterate every domain, not just legacy mentions.
      const domains = await this.getActiveDomainsForSchool(schoolId);

      // Parallel fetch with independent caching for each collection
      const [
        domainEvents,
        consequences,
        accountabilityPeriods,
        activeContract,
        messages
      ] = await Promise.all([
        this.getCachedOrFetch('domainEvents', studentId, () => this.fetchDomainEvents(studentId, schoolId, domains)),
        this.getCachedOrFetch('consequences', studentId, () => this.fetchConsequences(studentId, schoolId)),
        this.getCachedOrFetch('accountability', studentId, () => this.fetchAccountabilityPeriods(studentId, schoolId)),
        this.getCachedOrFetch('contracts', studentId, () => this.fetchActiveContract(studentId)),
        this.getCachedOrFetch('messages', studentId, () => this.fetchMessages(studentId, schoolId))
      ]);

      // Rule evaluator and other callers expect a flat `mentions` array for the
      // calling domain. Derive it from the domain map. Defaults to 'discipline'
      // (and falls back to it) so discipline's path is unchanged, while every
      // other domain counts its OWN events for threshold/count rules.
      const mentions = domainEvents[domainId] || domainEvents.discipline || [];

      const fetchTime = Date.now() - startTime;

      const totalDomainEvents = Object.values(domainEvents).reduce((n, arr) => n + arr.length, 0);

      disciplineLogger.info(`✅ Student data fetched in ${fetchTime}ms`, {
        studentId,
        domains: domains.length,
        domainEvents: totalDomainEvents,
        mentions: mentions.length,
        consequences: consequences.length,
        accountabilityPeriods: accountabilityPeriods.length,
        messages: messages.length,
        hasContract: !!activeContract,
        fetchTime
      });

      const result = {
        schoolId,  // Include schoolId for rule evaluation
        studentId,
        mentions,
        domainEvents,
        domains,
        consequences,
        accountabilityPeriods,
        activeContract,
        messages,
        fetchedAt: new Date(),
        stats: {
          totalMentions: mentions.length,
          totalDomainEvents,
          totalConsequences: consequences.length,
          totalMessages: messages.length,
          fetchTimeMs: fetchTime
        }
      };

      return result;
    } catch (error) {
      disciplineLogger.critical(`Failed to fetch student data`, { studentId, error: error.message });
      throw error;
    }
  }

  /**
   * Resolve the active domain configs for a school, with a legacy fallback
   * (`discipline` + `recognition`) for schools whose `domains` collection
   * hasn't been seeded yet. Always returns at least the two protected
   * domains so callers can rely on them being present.
   */
  async getActiveDomainsForSchool(schoolId) {
    if (!schoolId) return [];

    let domains = [];
    try {
      domains = await getDomains(schoolId);
    } catch (error) {
      disciplineLogger.warn('getDomains failed, falling back to protected definitions', {
        schoolId,
        error: error.message
      });
      domains = [];
    }

    const ensure = (id) => {
      if (domains.find(d => d.id === id)) return;
      const def = PROTECTED_DOMAIN_DEFINITIONS[id];
      if (def) domains.push({ id, ...def });
    };
    ensure('discipline');
    ensure('recognition');

    return domains
      .filter(d => d.active !== false)
      .sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99));
  }

  /**
   * Fetch all events for a student across every active domain.
   * Returns a `{[domainId]: events[]}` map. Events are sorted desc by
   * `issuedAt` (falling back to `createdAt`) and tagged with `domainId`
   * so consumers can render heterogeneous timelines without re-deriving it.
   *
   * @param {string} studentId
   * @param {string} schoolId
   * @param {Array<DomainConfig>} domains - active domains (caller-resolved)
   * @returns {Promise<Object<string, Array>>}
   */
  async fetchDomainEvents(studentId, schoolId, domains) {
    if (!schoolId) {
      disciplineLogger.warn('fetchDomainEvents called without schoolId', { studentId });
      return {};
    }
    if (!Array.isArray(domains) || domains.length === 0) {
      domains = await this.getActiveDomainsForSchool(schoolId);
    }

    const parentInfo = await this.checkIsParentWithLinks();
    const isParent = parentInfo && parentInfo.isParent;
    if (isParent && !parentInfo.linkedStudentIds.includes(studentId)) {
      disciplineLogger.warn('Parent trying to access unlinked student', {
        studentId,
        linkedIds: parentInfo.linkedStudentIds
      });
      return Object.fromEntries(domains.map(d => [d.id, []]));
    }

    const results = await Promise.all(domains.map(async (domain) => {
      const eventsCollection = domain?.collections?.events || domain?.eventsCollection;
      if (!eventsCollection) {
        return [domain.id, []];
      }

      try {
        let allDocs;
        if (isParent) {
          // Parent users can't filter by where(studentId) on Firestore arrays via rules,
          // so fetch the whole events collection and filter client-side. Mirrors fetchMentions.
          const allEventsQuery = query(
            collection(db, 'schools', schoolId, eventsCollection),
            ...dynamicDomainListConstraints({
              collectionName: eventsCollection,
              schoolId,
              domainId: domain.id,
            }),
          );
          const snapshot = await getDocs(allEventsQuery);
          allDocs = snapshot.docs.filter(doc => doc.data().studentId === studentId);
        } else {
          const eventsQuery = query(
            collection(db, 'schools', schoolId, eventsCollection),
            ...dynamicDomainListConstraints({
              collectionName: eventsCollection,
              schoolId,
              domainId: domain.id,
            }),
            where('studentId', '==', studentId)
          );
          const snapshot = await getDocs(eventsQuery);
          allDocs = snapshot.docs;
        }

        firebaseMonitor.logRead(
          eventsCollection,
          'getDocs',
          allDocs.length,
          `StudentDataFetcher.fetchDomainEvents[${domain.id}]`
        );

        const events = allDocs.map(d => {
          const data = d.data();
          const dateField = data.issuedAt || data.createdAt;
          return {
            id: d.id,
            ...data,
            domainId: domain.id,
            issuedAt: dateField?.toDate?.() || dateField,
            createdAt: data.createdAt?.toDate?.() || data.createdAt
          };
        });

        events.sort((a, b) => {
          const dateA = a.issuedAt || a.createdAt;
          const dateB = b.issuedAt || b.createdAt;
          const timeA = dateA?.getTime?.() || (dateA ? new Date(dateA).getTime() : 0);
          const timeB = dateB?.getTime?.() || (dateB ? new Date(dateB).getTime() : 0);
          return timeB - timeA;
        });

        return [domain.id, events];
      } catch (error) {
        // One missing/empty/unauthorized collection shouldn't poison the others.
        disciplineLogger.warn(`Failed to fetch events for domain ${domain.id}`, {
          studentId, schoolId, eventsCollection, error: error.message
        });
        return [domain.id, []];
      }
    }));

    return Object.fromEntries(results);
  }

  /**
   * Fetch all mentions for a student
   */
  async fetchMentions(studentId, schoolId) {
    // HIERARCHICAL ONLY: Migration completed, flat collection no longer used
    // Don't use orderBy - fetch all and sort client-side to include mentions without issuedAt

    if (!schoolId) {
      disciplineLogger.warn('fetchMentions called without schoolId', { studentId });
      return [];
    }

    // DISABLED: Parent check was breaking rule evaluation for staff with parent flag
    // Rule evaluation is ALWAYS a staff operation - parents don't issue mentions
    // TODO: Re-enable parent restrictions only for actual parent dashboard queries
    const parentInfo = false; // await this.checkIsParentWithLinks();

    let allDocs;
    if (parentInfo && parentInfo.isParent) {
      // Parent users: fetch ALL mentions and filter client-side
      // This is necessary because Firestore can't validate where clauses against arrays
      disciplineLogger.info('📚 Parent query: fetching all mentions for client-side filtering', {
        studentId,
        schoolId
      });

      // Check if this studentId is in parent's linkedStudentIds
      if (!parentInfo.linkedStudentIds.includes(studentId)) {
        disciplineLogger.warn('Parent trying to access unlinked student', {
          studentId,
          linkedIds: parentInfo.linkedStudentIds
        });
        return [];
      }

      const allMentionsQuery = query(
        collection(db, 'schools', schoolId, 'mentions')
        // No where clause for parents
      );
      const snapshot = await getDocs(allMentionsQuery);

      // Filter client-side to only include mentions for this student
      allDocs = snapshot.docs.filter(doc => doc.data().studentId === studentId);

      disciplineLogger.info('📚 Parent query filtered results', {
        totalDocs: snapshot.docs.length,
        filteredDocs: allDocs.length
      });
    } else {
      // Staff users: use normal where clause query
      const hierarchicalQuery = query(
        collection(db, 'schools', schoolId, 'mentions'),
        where('studentId', '==', studentId)
      );
      const hierarchicalSnapshot = await getDocs(hierarchicalQuery);
      allDocs = hierarchicalSnapshot.docs;
    }

    // Monitor the read
    firebaseMonitor.logRead('mentions', 'getDocs', allDocs.length, 'StudentDataFetcher.fetchMentions');

    const mentions = allDocs.map(doc => {
      const data = doc.data();
      // Use issuedAt if present, otherwise fall back to createdAt
      const dateField = data.issuedAt || data.createdAt;

      return {
        id: doc.id,
        ...data,
        issuedAt: dateField?.toDate?.() || dateField,
        createdAt: data.createdAt?.toDate?.() || data.createdAt
      };
    });

    // Sort client-side by issuedAt (falling back to createdAt) descending
    mentions.sort((a, b) => {
      const dateA = a.issuedAt || a.createdAt;
      const dateB = b.issuedAt || b.createdAt;
      const timeA = dateA?.getTime?.() || new Date(dateA).getTime();
      const timeB = dateB?.getTime?.() || new Date(dateB).getTime();
      return timeB - timeA; // Desc order
    });

    return mentions;
  }

  /**
   * Fetch all consequences (from disciplineQueue) for a student
   */
  async fetchConsequences(studentId, schoolId) {
    // HIERARCHICAL ONLY: Migration completed, flat collection no longer used

    if (!schoolId) {
      disciplineLogger.warn('fetchConsequences called without schoolId', { studentId });
      return [];
    }

    // Check if user is a parent
    const parentInfo = await this.checkIsParentWithLinks();

    let allDocs;
    if (parentInfo && parentInfo.isParent) {
      // Parent users: fetch ALL consequences and filter client-side
      disciplineLogger.info('📚 Parent query: fetching all consequences for client-side filtering', {
        studentId,
        schoolId
      });

      // Check if this studentId is in parent's linkedStudentIds
      if (!parentInfo.linkedStudentIds.includes(studentId)) {
        return [];
      }

      const allConsequencesQuery = query(
        collection(db, 'schools', schoolId, 'disciplineQueue')
        // No where clause for parents
      );
      const snapshot = await getDocs(allConsequencesQuery);

      // Filter client-side to only include consequences for this student
      allDocs = snapshot.docs.filter(doc => doc.data().studentId === studentId);

      disciplineLogger.info('📚 Parent query filtered consequences', {
        totalDocs: snapshot.docs.length,
        filteredDocs: allDocs.length
      });
    } else {
      // Staff users: use normal where clause query
      const hierarchicalQuery = query(
        collection(db, 'schools', schoolId, 'disciplineQueue'),
        where('studentId', '==', studentId)
      );
      const hierarchicalSnapshot = await getDocs(hierarchicalQuery);
      allDocs = hierarchicalSnapshot.docs;
    }

    // Monitor the read
    firebaseMonitor.logRead('disciplineQueue', 'getDocs', allDocs.length, 'StudentDataFetcher.fetchConsequences');

    // Sort by createdAt descending
    const consequences = allDocs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      issuedAt: doc.data().issuedAt?.toDate?.() || doc.data().issuedAt
    }));

    consequences.sort((a, b) => {
      const timeA = a.createdAt?.getTime?.() || new Date(a.createdAt).getTime();
      const timeB = b.createdAt?.getTime?.() || new Date(b.createdAt).getTime();
      return timeB - timeA; // Desc order
    });

    return consequences;
  }

  /**
   * Fetch accountability periods for a student
   */
  async fetchAccountabilityPeriods(studentId, schoolId) {
    if (!schoolId) {
      disciplineLogger.warn('fetchAccountabilityPeriods called without schoolId', { studentId });
      return [];
    }

    // Check if user is a parent
    const parentInfo = await this.checkIsParentWithLinks();

    let snapshot;
    if (parentInfo && parentInfo.isParent) {
      // Parent users: fetch ALL accountability periods and filter client-side
      disciplineLogger.info('📚 Parent query: fetching all accountability periods for client-side filtering', {
        studentId,
        schoolId
      });

      // Check if this studentId is in parent's linkedStudentIds
      if (!parentInfo.linkedStudentIds.includes(studentId)) {
        return [];
      }

      const allPeriodsQuery = query(
        collection(db, 'schools', schoolId, 'accountabilityPeriods')
        // No where clause for parents
      );
      const allSnapshot = await getDocs(allPeriodsQuery);

      // Filter client-side for this student and active status
      const filteredDocs = allSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.studentId === studentId && data.active === true;
      });

      disciplineLogger.info('📚 Parent query filtered accountability periods', {
        totalDocs: allSnapshot.docs.length,
        filteredDocs: filteredDocs.length
      });

      // Create a snapshot-like object with filtered docs
      snapshot = { docs: filteredDocs, size: filteredDocs.length };
    } else {
      // Staff users: use normal where clause query
      const q = query(
        collection(db, 'schools', schoolId, 'accountabilityPeriods'),
        where('studentId', '==', studentId),
        where('active', '==', true)
      );
      snapshot = await getDocs(q);
    }

    // Monitor the read
    firebaseMonitor.logRead('accountabilityPeriods', 'getDocs', snapshot.size, 'StudentDataFetcher.fetchAccountabilityPeriods');

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startDate: doc.data().startDate?.toDate?.() || doc.data().startDate,
      endDate: doc.data().endDate?.toDate?.() || doc.data().endDate
    }));
  }

  /**
   * Fetch active contract for a student (using cache)
   */
  async fetchActiveContract(studentId) {
    // Use the contract cache which handles:
    // - Global cache of all active contracts
    // - Individual caching with TTL
    // - "No contract" caching to avoid repeated queries
    const contract = await contractCache.getActiveContract(studentId);

    if (contract) {
      disciplineLogger.debug(`Found active contract for student ${studentId}`);
    }

    return contract;
  }

  /**
   * Fetch all messages for a student
   * Queries by studentId directly (root-level field)
   */
  async fetchMessages(studentId, schoolId) {
    disciplineLogger.debug(`🔍 Querying messages for studentId: ${studentId}`);

    if (!schoolId) {
      disciplineLogger.warn('fetchMessages called without schoolId', { studentId });
      return [];
    }

    // Check if user is a parent
    const parentInfo = await this.checkIsParentWithLinks();

    let snapshot;
    if (parentInfo && parentInfo.isParent) {
      // Parent users: fetch ALL messages and filter client-side
      disciplineLogger.info('📚 Parent query: fetching all messages for client-side filtering', {
        studentId,
        schoolId
      });

      // Check if this studentId is in parent's linkedStudentIds
      if (!parentInfo.linkedStudentIds.includes(studentId)) {
        return [];
      }

      const allMessagesQuery = query(
        collection(db, 'schools', schoolId, 'messages')
        // No where clause for parents
      );
      const allSnapshot = await getDocs(allMessagesQuery);

      // Filter client-side to only include messages for this student
      const filteredDocs = allSnapshot.docs.filter(doc => doc.data().studentId === studentId);

      disciplineLogger.info('📚 Parent query filtered messages', {
        totalDocs: allSnapshot.docs.length,
        filteredDocs: filteredDocs.length
      });

      // Create a snapshot-like object with filtered docs
      snapshot = { docs: filteredDocs };
    } else {
      // Staff users: use normal where clause query
      const q = query(
        collection(db, 'schools', schoolId, 'messages'),
        where('studentId', '==', studentId)
      );
      snapshot = await getDocs(q);
    }

    // Monitor the read
    firebaseMonitor.logRead('messages', 'getDocs', snapshot.size, 'StudentDataFetcher.fetchMessages');

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      respondedAt: doc.data().delivery?.telegram?.respondedAt?.toDate?.() || doc.data().delivery?.telegram?.respondedAt
    }));
  }

  /**
   * Filter consequences by date range (client-side)
   */
  filterConsequencesByDateRange(consequences, startDate, endDate) {
    return consequences.filter(c => {
      const date = c.issuedAt || c.createdAt;
      return date >= startDate && date <= endDate;
    });
  }

  /**
   * Filter consequences by type (client-side)
   */
  filterConsequencesByType(consequences, types) {
    const typeArray = Array.isArray(types) ? types : [types];
    return consequences.filter(c => {
      // Check both type and typeId for compatibility
      return typeArray.some(type =>
        c.type === type ||
        c.typeId === type ||
        c.consequenceType === type
      );
    });
  }

  /**
   * Count unconsumed consequences (client-side)
   */
  countUnconsumedConsequences(consequences) {
    return consequences.filter(c =>
      !c.usedByThresholdRule &&
      !c.usedByEscalationRule &&
      !c.undone
    ).length;
  }

  /**
   * Clear the ASSEMBLED student-profile blob that useStudentData caches in
   * localStorage under `swan_student_data_<schoolId>_<studentId>` (see
   * useStudentData.js). The learner-only key is retained here as a legacy
   * cleanup boundary.
   *
   * That hook is a SECOND cache layer sitting in front of this fetcher: on
   * navigation it serves its localStorage blob and skips the Firestore fetch
   * entirely (1h TTL), so invalidating only our per-collection caches is not
   * enough — a freshly-issued offence won't appear until the TTL expires or
   * refresh() runs. Every write path already funnels through our invalidate*
   * methods, so dropping the blob here keeps both layers consistent.
   *
   * Must be UNCONDITIONAL: on a fresh page load our in-memory caches are empty
   * (so the `*Cache.has()` checks below are false), yet the localStorage blob
   * persists across reloads and would otherwise stay stale.
   * @param {string} studentId
   */
  clearAssembledProfileCache(studentId) {
    try {
      if (typeof localStorage !== 'undefined' && studentId) {
        const prefix = 'swan_student_data_';
        const legacyKey = `${prefix}${studentId}`;
        const schoolScopedSuffix = `_${studentId}`;

        Object.keys(localStorage)
          .filter(key => key === legacyKey || (
            key.startsWith(prefix) && key.endsWith(schoolScopedSuffix)
          ))
          .forEach(key => localStorage.removeItem(key));
      }
    } catch (e) {
      // Ignore storage errors (quota / disabled storage)
    }
  }

  /**
   * Invalidate consequences cache for a specific student
   * Use after creating/updating consequences
   * Clears both memory and sessionStorage caches
   * @param {string} studentId - The student ID to invalidate
   */
  invalidateConsequencesCache(studentId) {
    this.clearAssembledProfileCache(studentId);
    // A page restored from the assembled profile cache has no memory entry,
    // but its session cache can still contain the consequence before editing.
    this.clearSessionStorage('consequences', studentId);
    if (this.consequencesCache.has(studentId)) {
      this.consequencesCache.delete(studentId);
      disciplineLogger.debug(`🗑️ Consequences cache invalidated for student ${studentId}`);
    }
  }

  /**
   * Invalidate mentions cache for a specific student
   * Use after creating/updating mentions
   * Clears both memory and sessionStorage caches
   * @param {string} studentId - The student ID to invalidate
   */
  invalidateMentionsCache(studentId) {
    this.clearAssembledProfileCache(studentId);
    let invalidated = false;
    if (this.mentionsCache.has(studentId)) {
      this.mentionsCache.delete(studentId);
      this.clearSessionStorage('mentions', studentId);
      invalidated = true;
    }
    // Mentions live inside the unified domainEvents cache (discipline domain).
    // Both must be cleared together or the new mention won't appear on the profile.
    if (this.domainEventsCache.has(studentId)) {
      this.domainEventsCache.delete(studentId);
      this.clearSessionStorage('domainEvents', studentId);
      invalidated = true;
    }
    if (invalidated) {
      disciplineLogger.debug(`🗑️ Mentions/domainEvents cache invalidated for student ${studentId}`);
    }
  }

  /**
   * Invalidate domain events cache for a specific student.
   * Use after creating/updating an event in any domain (incl. recognitions).
   */
  invalidateDomainEventsCache(studentId) {
    this.clearAssembledProfileCache(studentId);
    if (this.domainEventsCache.has(studentId)) {
      this.domainEventsCache.delete(studentId);
      this.clearSessionStorage('domainEvents', studentId);
      disciplineLogger.debug(`🗑️ Domain events cache invalidated for student ${studentId}`);
    }
  }

  /**
   * Invalidate accountability cache for a specific student
   * Use after creating/updating accountability periods
   * Clears both memory and sessionStorage caches
   * @param {string} studentId - The student ID to invalidate
   */
  invalidateAccountabilityCache(studentId) {
    if (this.accountabilityCache.has(studentId)) {
      this.accountabilityCache.delete(studentId);
      this.clearSessionStorage('accountability', studentId);
      disciplineLogger.debug(`🗑️ Accountability cache invalidated for student ${studentId}`);
    }
  }

  /**
   * Invalidate messages cache for a specific student
   * Use after creating/updating messages
   * Clears both memory and sessionStorage caches
   * @param {string} studentId - The student ID to invalidate
   */
  invalidateMessagesCache(studentId) {
    this.clearAssembledProfileCache(studentId);
    if (this.messagesCache.has(studentId)) {
      this.messagesCache.delete(studentId);
      this.clearSessionStorage('messages', studentId);
      disciplineLogger.debug(`🗑️ Messages cache invalidated for student ${studentId}`);
    }
  }

  /**
   * Invalidate all caches for a specific student
   * Forces next fetchStudentData call to fetch fresh data for all collections
   * Clears both memory and sessionStorage caches
   * @param {string} studentId - The student ID to invalidate
   */
  invalidateCache(studentId) {
    this.clearAssembledProfileCache(studentId);
    this.clearSessionStorage('consequences', studentId);
    let invalidated = false;

    if (this.mentionsCache.has(studentId)) {
      this.mentionsCache.delete(studentId);
      this.clearSessionStorage('mentions', studentId);
      invalidated = true;
    }
    if (this.domainEventsCache.has(studentId)) {
      this.domainEventsCache.delete(studentId);
      this.clearSessionStorage('domainEvents', studentId);
      invalidated = true;
    }
    if (this.consequencesCache.has(studentId)) {
      this.consequencesCache.delete(studentId);
      invalidated = true;
    }
    if (this.accountabilityCache.has(studentId)) {
      this.accountabilityCache.delete(studentId);
      this.clearSessionStorage('accountability', studentId);
      invalidated = true;
    }
    if (this.contractsCache.has(studentId)) {
      this.contractsCache.delete(studentId);
      this.clearSessionStorage('contracts', studentId);
      invalidated = true;
    }
    if (this.messagesCache.has(studentId)) {
      this.messagesCache.delete(studentId);
      this.clearSessionStorage('messages', studentId);
      invalidated = true;
    }

    if (invalidated) {
      disciplineLogger.debug(`🗑️ All caches (memory + sessionStorage) invalidated for student ${studentId}`);
    }
  }

  /**
   * Filter mentions by date range (client-side)
   */
  filterMentionsByDateRange(mentions, startDate, endDate) {
    return mentions.filter(m => {
      const date = m.issuedAt;
      return date >= startDate && date <= endDate;
    });
  }

  /**
   * Filter mentions by offense (client-side)
   */
  filterMentionsByOffense(mentions, offense) {
    return mentions.filter(m =>
      m.offence === offense ||
      m.reason === offense ||
      m.offense === offense
    );
  }
}

// Export singleton instance
export const studentDataFetcher = new StudentDataFetcher();
